/**
 * Translates Event Registration V2 in-memory state into the shared
 * `<CheckoutShell>` `Cart` value object.
 *
 * Pure function. No React, no API calls, no side effects. The Phase 2
 * adapter component composes this with `useRegistration()` state and
 * permission checks, then hands the resulting Cart to the shell.
 *
 * Responsibilities:
 *   - One CartLine per paid (non-waitlisted) attendee.
 *   - Discount lines for vouchers, promo code, admin discount, OR
 *     Total Access coverage (mutually exclusive with the others — when
 *     LATA-covered, the whole subtotal is one "other"-source discount and
 *     the cart hides further discount UI).
 *   - Metadata flags (acceptsVouchers, acceptsDiscountCode, adminCanDiscount,
 *     needsShipping) derived from event config + caller-supplied
 *     permission flag.
 *   - Reuses the existing `calculateCheckoutPrice` util for the pricing
 *     math. This file does NOT duplicate the math — it shapes the result.
 *
 * What it does NOT do:
 *   - Track waitlisted attendees (the adapter renders that as a slot banner
 *     above/below the cart, since waitlist is a domain-specific concept the
 *     shell can't model as cart metadata).
 *   - Validate. Garbage in = garbage out. Validation lives in the API
 *     boundary (parseCart for incoming, server-side for outgoing submits).
 */

import { Cart, CartLine, DiscountLine } from "@/modules/checkout/types";

import type { Attendee } from "../context/RegistrationContext";
import type { EventSessionRegistrationInfoData } from "../api/getEventSessionRegistrationInfo/types";
import {
  calculateCheckoutPrice,
  DiscountInfo,
} from "./checkoutPriceCalculator";
import { stageMembershipLabel } from "./stageMembershipLabel";

export type BuildEventCartInput = {
  eventInfo: EventSessionRegistrationInfoData;
  attendees: Attendee[];
  voucherQuantity: number;
  discountCode: string | null;
  discountValueType: "percentage" | "fixed_amount" | null;
  discountRawValue: number;
  adminDiscountType: "percentage" | "fixed_amount" | null;
  adminDiscountValue: number;
  adminDiscountReason: string;
  // Caller passes true when the current user has the
  // `administration-events` permission. Domain owns the permission check;
  // this util just trusts the flag.
  adminCanDiscount: boolean;
  // The company's effective stage name (userAppSettings.companyStage),
  // shown on the stage-coverage line. Null falls back to generic copy.
  stageName?: string | null;
};

/**
 * The sku used on every event CartLine. Events don't have per-attendee
 * SKUs (there's one event being sold, attendees are just seat slots), so
 * we use the event UUID. CARA-side this maps to whatever billing SKU the
 * event resolves to via its product mapping.
 */
const eventSku = (eventInfo: EventSessionRegistrationInfoData): string =>
  eventInfo.eventUuid ?? "EVENT-UNKNOWN";

const eventLineName = (eventInfo: EventSessionRegistrationInfoData): string => {
  const name = eventInfo.eventName ?? "Event";
  if (eventInfo.eventSessionName) {
    return `${name} — ${eventInfo.eventSessionName}`;
  }
  return name;
};

/**
 * True only when vouchers are genuinely usable: the event must be
 * voucher-eligible AND the company must have voucher seats left.
 * `isVoucherEligible` alone isn't enough — a company can exhaust its
 * voucher seats while the event stays eligible.
 */
// Seats already paid for by a membership (stage or Total Access):
// no charge, and voucher / discount UIs are suppressed.
const isCoveredByMembership = (
  eventInfo: EventSessionRegistrationInfoData,
): boolean => eventInfo.isCoveredByTotalAccess || eventInfo.isCoveredByStage;

const hasVoucherCoverage = (
  eventInfo: EventSessionRegistrationInfoData,
): boolean =>
  eventInfo.isVoucherEligible && eventInfo.companyAvailableVoucherSeats > 0;

const codeDiscountInfo = (input: BuildEventCartInput): DiscountInfo | null => {
  if (!input.discountValueType || input.discountRawValue <= 0) return null;
  return {
    type: input.discountValueType,
    value: input.discountRawValue,
  };
};

const adminDiscountInfo = (input: BuildEventCartInput): DiscountInfo | null => {
  if (!input.adminDiscountType || input.adminDiscountValue <= 0) return null;
  return {
    type: input.adminDiscountType,
    value: input.adminDiscountValue,
  };
};

const buildLines = (
  input: BuildEventCartInput,
  eventPrice: number,
): CartLine[] => {
  const sku = eventSku(input.eventInfo);
  const name = eventLineName(input.eventInfo);
  const isMembershipCovered = isCoveredByMembership(input.eventInfo);

  const paid = input.attendees.filter((a) => a.isSelected && !a.isWaitlist);
  return paid.map((attendee, idx) => ({
    sku,
    name,
    description: input.eventInfo.venue?.name ?? undefined,
    quantity: 1,
    unitPrice: eventPrice,
    metadata: {
      requiresShipping: false,
      voucherEligible:
        hasVoucherCoverage(input.eventInfo) && !isMembershipCovered,
      discountCodeEligible: !isMembershipCovered,
    },
    domainData: {
      attendeeIndex: idx,
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
    },
  }));
};

const buildDiscountLines = (
  input: BuildEventCartInput,
  calc: ReturnType<typeof calculateCheckoutPrice>,
): DiscountLine[] => {
  // Membership coverage (stage or Total Access) is mutually exclusive
  // with vouchers / code / admin discounts: the whole subtotal is
  // covered, so one "other" line tells the user why.
  if (input.eventInfo.isCoveredByStage) {
    if (calc.baseAmount <= 0) return [];
    const membership = stageMembershipLabel(input.stageName);
    return [
      {
        sku: "STAGE-MEMBERSHIP",
        name: `${membership.charAt(0).toUpperCase()}${membership.slice(1)} coverage`,
        description: `Included with your ${membership}`,
        amount: calc.baseAmount,
        source: "other",
      },
    ];
  }

  if (input.eventInfo.isCoveredByTotalAccess) {
    if (calc.baseAmount <= 0) return [];
    return [
      {
        sku: "TOTAL-ACCESS",
        name: "Total Access coverage",
        description: "Included with your Total Access subscription",
        amount: calc.baseAmount,
        source: "other",
      },
    ];
  }

  const lines: DiscountLine[] = [];
  if (calc.voucherDiscount > 0) {
    const usedVouchers = Math.min(
      input.voucherQuantity,
      calc.paidAttendeeCount,
    );
    lines.push({
      sku: "VOUCHER",
      name: usedVouchers === 1 ? "Voucher applied" : "Vouchers applied",
      description:
        usedVouchers === 1
          ? "1 voucher applied"
          : `${usedVouchers} vouchers applied`,
      amount: calc.voucherDiscount,
      source: "voucher",
    });
  }
  if (calc.codeDiscount > 0 && input.discountCode) {
    lines.push({
      sku: input.discountCode,
      name: `Promo code ${input.discountCode}`,
      amount: calc.codeDiscount,
      source: "discount_code",
    });
  }
  if (calc.adminDiscount > 0) {
    lines.push({
      sku: "ADMIN-DISCOUNT",
      name: "Admin discount",
      description: input.adminDiscountReason || undefined,
      amount: calc.adminDiscount,
      source: "admin_discount",
    });
  }
  return lines;
};

export function buildEventCart(input: BuildEventCartInput): Cart {
  const eventPrice = input.eventInfo.eventPrice ?? 0;
  const isMembershipCovered = isCoveredByMembership(input.eventInfo);

  const calc = calculateCheckoutPrice(
    input.attendees,
    eventPrice,
    input.voucherQuantity,
    codeDiscountInfo(input),
    adminDiscountInfo(input),
  );

  const lines = buildLines(input, eventPrice);
  const discountLines = buildDiscountLines(input, calc);

  const subtotal = calc.baseAmount;
  const total = isMembershipCovered ? 0 : calc.total;

  // Discount + voucher + admin-discount UIs only make sense when
  // there's something to actually discount. Hide them when:
  //  - event is free (no list price), or
  //  - event is fully membership-covered (subtotal already $0), or
  //  - every selected attendee is waitlisted (no paid lines).
  const hasPaidAttendees = calc.paidAttendeeCount > 0;

  return {
    lines,
    discountLines,
    subtotal,
    total,
    needsShipping: false,
    acceptsDiscountCode:
      !isMembershipCovered && eventPrice > 0 && hasPaidAttendees,
    acceptsVouchers:
      hasVoucherCoverage(input.eventInfo) &&
      !isMembershipCovered &&
      eventPrice > 0 &&
      hasPaidAttendees,
    adminCanDiscount:
      input.adminCanDiscount &&
      !isMembershipCovered &&
      eventPrice > 0 &&
      hasPaidAttendees,
  };
}
